import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    adminStats,
    adminLeads,
    adminBookings,
    adminNewsletter,
    adminUpdateLead,
    adminDeleteLead,
    adminUpdateBooking,
    adminDeleteBooking,
    clearAdminToken,
} from "@/lib/api";
import {
    LogOut,
    Users,
    CalendarCheck,
    Mail,
    TrendingUp,
    Trash2,
    CheckCircle2,
    Clock,
    Download,
    Search,
    X,
} from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const LEAD_STATUSES = ["new", "contacted", "qualified", "converted", "lost"];
const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("leads");
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [subs, setSubs] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [s, l, b, n] = await Promise.all([
                adminStats(),
                adminLeads(),
                adminBookings(),
                adminNewsletter(),
            ]);
            setStats(s);
            setLeads(l);
            setBookings(b);
            setSubs(n);
        } catch (e) {
            toast.error("Failed to load dashboard data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const onLogout = () => {
        clearAdminToken();
        toast.success("Logged out.");
        navigate("/admin/login", { replace: true });
    };

    const filteredLeads = useMemo(() => {
        if (!query) return leads;
        const q = query.toLowerCase();
        return leads.filter(
            (l) =>
                (l.name || "").toLowerCase().includes(q) ||
                (l.email || "").toLowerCase().includes(q) ||
                (l.country || "").toLowerCase().includes(q) ||
                (l.interest || "").toLowerCase().includes(q),
        );
    }, [leads, query]);

    const filteredBookings = useMemo(() => {
        if (!query) return bookings;
        const q = query.toLowerCase();
        return bookings.filter(
            (b) =>
                (b.name || "").toLowerCase().includes(q) ||
                (b.email || "").toLowerCase().includes(q),
        );
    }, [bookings, query]);

    const updateLead = async (id, body) => {
        try {
            await adminUpdateLead(id, body);
            setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, ...body } : l)));
            toast.success("Updated.");
        } catch {
            toast.error("Update failed.");
        }
    };
    const removeLead = async (id) => {
        if (!window.confirm("Delete this lead?")) return;
        try {
            await adminDeleteLead(id);
            setLeads((arr) => arr.filter((l) => l.id !== id));
            toast.success("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };
    const updateBooking = async (id, body) => {
        try {
            await adminUpdateBooking(id, body);
            setBookings((arr) => arr.map((b) => (b.id === id ? { ...b, ...body } : b)));
            toast.success("Updated.");
        } catch {
            toast.error("Update failed.");
        }
    };
    const removeBooking = async (id) => {
        if (!window.confirm("Delete this booking?")) return;
        try {
            await adminDeleteBooking(id);
            setBookings((arr) => arr.filter((b) => b.id !== id));
            toast.success("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    const exportCSV = (rows, name) => {
        if (!rows.length) return toast.message("Nothing to export.");
        const keys = Object.keys(rows[0]);
        const csv = [
            keys.join(","),
            ...rows.map((r) =>
                keys
                    .map((k) =>
                        JSON.stringify(r[k] ?? "").replace(/\\n/g, " "),
                    )
                    .join(","),
            ),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <main
            className="min-h-screen bg-[#0A1547] text-[#F3F4F6]"
            data-testid="admin-dashboard-page"
        >
            {/* Top bar */}
            <div className="border-b border-white/5 glass sticky top-0 z-40">
                <div className="container-x flex items-center justify-between py-4">
                    <div className="flex items-center gap-6">
                        <Link to="/" data-testid="admin-logo-link">
                            <Logo height={56} />
                        </Link>
                        <div className="hidden md:block h-8 w-px bg-white/10" />
                        <div className="hidden md:block">
                            <div className="overline">Admin Console</div>
                            <div className="font-serif-display text-lg">Operations Dashboard</div>
                        </div>
                    </div>
                    <button
                        onClick={onLogout}
                        className="btn-outline-brass"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </div>

            <div className="container-x py-10 space-y-10">
                {/* Stats */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
                    <StatCard
                        icon={<Users size={20} />}
                        label="Total Leads"
                        value={stats?.total_leads ?? "—"}
                        sub={`${stats?.leads_this_week ?? 0} this week`}
                    />
                    <StatCard
                        icon={<CalendarCheck size={20} />}
                        label="Total Bookings"
                        value={stats?.total_bookings ?? "—"}
                        sub={`${stats?.pending_bookings ?? 0} pending`}
                    />
                    <StatCard
                        icon={<Mail size={20} />}
                        label="Subscribers"
                        value={stats?.total_subscribers ?? "—"}
                    />
                    <StatCard
                        icon={<TrendingUp size={20} />}
                        label="Notifications to"
                        value={
                            <span className="text-base">
                                {stats?.notification_email ?? "—"}
                            </span>
                        }
                        sub="Configure in .env"
                    />
                </section>

                {/* Tabs + search */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex border-b border-white/10">
                        <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testId="admin-tab-leads">
                            Leads <span className="text-xs ml-2 opacity-70">{leads.length}</span>
                        </TabBtn>
                        <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testId="admin-tab-bookings">
                            Bookings <span className="text-xs ml-2 opacity-70">{bookings.length}</span>
                        </TabBtn>
                        <TabBtn active={tab === "subs"} onClick={() => setTab("subs")} testId="admin-tab-subs">
                            Newsletter <span className="text-xs ml-2 opacity-70">{subs.length}</span>
                        </TabBtn>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-[#64748B]" />
                            <input
                                placeholder="Search..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="np-input pl-9 py-2 w-64"
                                data-testid="admin-search-input"
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    className="absolute top-1/2 -translate-y-1/2 right-3 text-[#64748B] hover:text-[#C8A064]"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() =>
                                exportCSV(
                                    tab === "leads" ? leads : tab === "bookings" ? bookings : subs,
                                    tab,
                                )
                            }
                            className="btn-outline-brass !py-2"
                            data-testid="admin-export-btn"
                        >
                            <Download size={14} /> Export
                        </button>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="card-solid p-10 text-center text-[#94A3B8]">Loading data...</div>
                ) : tab === "leads" ? (
                    <LeadsTable
                        rows={filteredLeads}
                        onStatus={(id, s) => updateLead(id, { status: s })}
                        onDelete={removeLead}
                    />
                ) : tab === "bookings" ? (
                    <BookingsTable
                        rows={filteredBookings}
                        onStatus={(id, s) => updateBooking(id, { status: s })}
                        onDelete={removeBooking}
                    />
                ) : (
                    <SubsTable rows={subs.filter((s) =>
                        !query || s.email.toLowerCase().includes(query.toLowerCase())
                    )} />
                )}
            </div>
        </main>
    );
}

const StatCard = ({ icon, label, value, sub }) => (
    <div className="card-solid p-6">
        <div className="flex items-start justify-between mb-4">
            <div className="overline">{label}</div>
            <div className="text-[#C8A064]">{icon}</div>
        </div>
        <div className="font-serif-display text-3xl text-[#F3F4F6] leading-none">{value}</div>
        {sub && <div className="text-xs text-[#64748B] mt-2">{sub}</div>}
    </div>
);

const TabBtn = ({ active, onClick, children, testId }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className={`px-5 py-3 text-sm tracking-wide transition-colors border-b-2 -mb-px ${
            active
                ? "border-[#C8A064] text-[#C8A064]"
                : "border-transparent text-[#94A3B8] hover:text-[#F3F4F6]"
        }`}
    >
        {children}
    </button>
);

const StatusBadge = ({ value, options, onChange }) => (
    <select
        value={value || options[0]}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-[#C8A064]/30 text-[#C8A064] text-xs px-2 py-1 focus:outline-none focus:border-[#C8A064]"
    >
        {options.map((o) => (
            <option key={o} value={o} className="bg-[#0A1547]">
                {o}
            </option>
        ))}
    </select>
);

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
};

const LeadsTable = ({ rows, onStatus, onDelete }) => (
    <div className="card-solid overflow-x-auto" data-testid="admin-leads-table">
        {!rows.length ? (
            <div className="p-10 text-center text-[#94A3B8]">No leads yet.</div>
        ) : (
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left border-b border-white/10 text-[#94A3B8] text-xs uppercase tracking-wider">
                        <th className="p-4">Date</th>
                        <th className="p-4">Name</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Phone</th>
                        <th className="p-4">Country</th>
                        <th className="p-4">Interest</th>
                        <th className="p-4">Status</th>
                        <th className="p-4"></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr
                            key={r.id}
                            className="border-b border-white/5 hover:bg-white/[0.02]"
                            data-testid={`lead-row-${r.id}`}
                        >
                            <td className="p-4 text-[#94A3B8] whitespace-nowrap">{fmt(r.created_at)}</td>
                            <td className="p-4 text-[#F3F4F6] font-medium">{r.name}</td>
                            <td className="p-4 text-[#C8A064]">
                                <a href={`mailto:${r.email}`} className="hover:underline">
                                    {r.email}
                                </a>
                            </td>
                            <td className="p-4 text-[#94A3B8]">{r.phone || "—"}</td>
                            <td className="p-4 text-[#94A3B8]">{r.country || "—"}</td>
                            <td className="p-4 text-[#94A3B8]">{r.interest || "—"}</td>
                            <td className="p-4">
                                <StatusBadge
                                    value={r.status}
                                    options={LEAD_STATUSES}
                                    onChange={(v) => onStatus(r.id, v)}
                                />
                            </td>
                            <td className="p-4">
                                <button
                                    onClick={() => onDelete(r.id)}
                                    className="text-[#94A3B8] hover:text-red-400 transition-colors"
                                    data-testid={`lead-delete-${r.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const BookingsTable = ({ rows, onStatus, onDelete }) => (
    <div className="card-solid overflow-x-auto" data-testid="admin-bookings-table">
        {!rows.length ? (
            <div className="p-10 text-center text-[#94A3B8]">No bookings yet.</div>
        ) : (
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left border-b border-white/10 text-[#94A3B8] text-xs uppercase tracking-wider">
                        <th className="p-4">Created</th>
                        <th className="p-4">Name</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Date</th>
                        <th className="p-4">Time</th>
                        <th className="p-4">Timezone</th>
                        <th className="p-4">Status</th>
                        <th className="p-4"></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr
                            key={r.id}
                            className="border-b border-white/5 hover:bg-white/[0.02]"
                            data-testid={`booking-row-${r.id}`}
                        >
                            <td className="p-4 text-[#94A3B8] whitespace-nowrap">{fmt(r.created_at)}</td>
                            <td className="p-4 text-[#F3F4F6] font-medium">{r.name}</td>
                            <td className="p-4 text-[#C8A064]">
                                <a href={`mailto:${r.email}`} className="hover:underline">
                                    {r.email}
                                </a>
                            </td>
                            <td className="p-4 text-[#F3F4F6] flex items-center gap-2">
                                <CheckCircle2 size={12} className="text-[#C8A064]" /> {r.preferred_date}
                            </td>
                            <td className="p-4 text-[#F3F4F6] flex items-center gap-2">
                                <Clock size={12} className="text-[#C8A064]" /> {r.preferred_time}
                            </td>
                            <td className="p-4 text-[#94A3B8] text-xs">{r.timezone_str || "—"}</td>
                            <td className="p-4">
                                <StatusBadge
                                    value={r.status}
                                    options={BOOKING_STATUSES}
                                    onChange={(v) => onStatus(r.id, v)}
                                />
                            </td>
                            <td className="p-4">
                                <button
                                    onClick={() => onDelete(r.id)}
                                    className="text-[#94A3B8] hover:text-red-400 transition-colors"
                                    data-testid={`booking-delete-${r.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const SubsTable = ({ rows }) => (
    <div className="card-solid overflow-x-auto" data-testid="admin-subs-table">
        {!rows.length ? (
            <div className="p-10 text-center text-[#94A3B8]">No subscribers yet.</div>
        ) : (
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left border-b border-white/10 text-[#94A3B8] text-xs uppercase tracking-wider">
                        <th className="p-4">Subscribed</th>
                        <th className="p-4">Email</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-white/5">
                            <td className="p-4 text-[#94A3B8] whitespace-nowrap">{fmt(r.created_at)}</td>
                            <td className="p-4 text-[#C8A064]">{r.email}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);
